import { memo, useEffect, useRef } from 'react'

import { FormControlLabel, Switch } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useDrmProviderUpdateMutation } from 'hooks/mutations/content'
import { drmProviderIndexRoute } from 'pages/NWPlatform/DrmProviders/DrmProvider'
import { drmProviderQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const navigate = useNavigate({ from: drmProviderIndexRoute.fullPath })

  const { drmProviderId } = drmProviderIndexRoute.useParams()
  const { sensitive } = drmProviderIndexRoute.useSearch()

  const drmProviderQuery = useQuery(drmProviderQueryKeys.detail({ drmProviderId, sensitive }))
  const { data: drmProvider } = drmProviderQuery

  const { mutate } = useDrmProviderUpdateMutation()

  useEffect(() => {
    if (editorInstanceRef.current && drmProviderQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(drmProvider, null, 4))
    }
  }, [drmProviderQuery.isSuccess, drmProvider])

  if (drmProviderQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!drmProvider) {
    return <NotFound />
  }

  return (
    <Editor
      CustomActionsSlot={
        <FormControlLabel
          control={
            <Switch checked={sensitive} onChange={(_, checked) => navigate({ search: { sensitive: checked } })} />
          }
          label={t('domain:DrmProvider.sensitive')}
          labelPlacement="start"
        />
      }
      value={drmProvider}
      onSubmit={(data) => {
        mutate(
          {
            drmProviderId,
            drmProviderEdit: {
              ...(data as unknown as Record<string, unknown>),
            },
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('domain:DrmProvider.saved'), {
                variant: 'success',
              })
            },
          },
        )
      }}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
